import React, { useState, useEffect, useRef } from 'react';
import './RandomnessMonitor.css';
import apiService from '../services/api';
import WizardSpinner from './WizardSpinner';

interface TestResult {
  test_name: string;
  passed: boolean;
  p_value?: number;
  sample_size: number;
  timestamp: string;
}

interface QualityReport {
  status: string;
  sample_size: number;
  tests_run: number;
  tests_passed: number;
  pass_rate: number;
  recommendation: string;
  results?: TestResult[];
  message?: string;
  minimum_required?: number;
}

interface MonitoringStatus {
  monitoring_active: boolean;
  data_points_collected: number;
  minimum_required: number;
  sufficient_for_testing: boolean;
  recommendation: string;
}

const RandomnessMonitor: React.FC = () => {
  const [qualityReport, setQualityReport] = useState<QualityReport | null>(null);
  const [monitoringStatus, setMonitoringStatus] = useState<MonitoringStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [collectingData, setCollectingData] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  
  // Ref to track if component is mounted
  const isMountedRef = useRef(true);

  const fetchData = async () => {
    try {
      if (isMountedRef.current) {
        setLoading(true);
        setError(null);
      }

      // Fetch quality report using api service (includes auth interceptor)
      const reportData = await apiService.getRandomnessQualityReport(7);
      
      // Fetch monitoring status using api service (includes auth interceptor)
      const statusData = await apiService.getRandomnessMonitoringStatus();

      // Only update state if component is still mounted
      if (isMountedRef.current) {
        setQualityReport(reportData);
        setMonitoringStatus(statusData);
        setLastUpdated(new Date());
      }
    } catch (err) {
      if (isMountedRef.current) {
        setError('Failed to fetch randomness monitoring data');
      }
      // console.error('Error fetching data:', err);
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    // Set mounted flag
    isMountedRef.current = true;
    
    fetchData();
    // Refresh every 30 seconds
    const interval = setInterval(fetchData, 30000);
    
    return () => {
      // Cleanup: set mounted flag to false and clear interval
      isMountedRef.current = false;
      clearInterval(interval);
    };
  }, []);

  const getPassRateColor = (passRate: number) => {
    if (passRate >= 0.9) return '#28a745'; // Green
    if (passRate >= 0.8) return '#ffc107'; // Yellow
    return '#dc3545'; // Red
  };

  const formatPValue = (pValue?: number) => {
    if (pValue === undefined || pValue === null) return 'N/A';
    return pValue.toFixed(6);
  };

  const formatSampleSize = (size: number) => {
    if (size >= 1000000) {
      return `${(size / 1000000).toFixed(1)}M bits`;
    } else if (size >= 1000) {
      return `${(size / 1000).toFixed(1)}K bits`;
    }
    return `${size} bits`;
  };

  if (loading && !qualityReport) {
    return (
      <div className="randomness-monitor">
        <div 
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 'var(--plexus-space-xl)',
            minHeight: '400px',
          }}
        >
          <WizardSpinner size={50} dataTestId="randomness-monitor-loading" />
          <p style={{ 
            marginTop: 'var(--plexus-space-md)',
            color: 'var(--plexus-color-neutral-300)',
          }}>
            Loading randomness monitoring data...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="randomness-monitor">
      <div className="monitor-header">
        <h2>Randomness Quality Monitor</h2>
        <div className="header-controls">
          <button onClick={fetchData} disabled={loading} className="refresh-btn">
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
          {lastUpdated && (
            <span className="last-updated">
              Last updated: {lastUpdated.toLocaleTimeString()}
            </span>
          )}
        </div>
      </div>

      {error && (
        <div className="error-message">
          <p>{error}</p>
          <button onClick={fetchData} className="retry-btn">
            Retry
          </button>
        </div>
      )}

      {/* Monitoring Status */}
      {monitoringStatus && (
        <div className="status-card">
          <h3>Monitoring Status</h3>
          <div className="status-grid">
            <div className="status-item">
              <span className="label">Status:</span>
              <span className={`value ${monitoringStatus.monitoring_active ? 'active' : 'inactive'}`}>
                {monitoringStatus.monitoring_active ? 'Active' : 'Inactive'}
              </span>
            </div>
            <div className="status-item">
              <span className="label">Data Points:</span>
              <span className="value">{monitoringStatus.data_points_collected.toLocaleString()}</span>
            </div>
            <div className="status-item">
              <span className="label">Minimum Required:</span>
              <span className="value">{monitoringStatus.minimum_required.toLocaleString()}</span>
            </div>
            <div className="status-item">
              <span className="label">Sufficient for Testing:</span>
              <span className={`value ${monitoringStatus.sufficient_for_testing ? 'yes' : 'no'}`}>
                {monitoringStatus.sufficient_for_testing ? 'Yes' : 'No'}
              </span>
            </div>
          </div>
          <p className="recommendation">{monitoringStatus.recommendation}</p>
        </div>
      )}

      {/* Quality Report */}
      {qualityReport && (
        <div className="quality-report">
          <h3>Quality Report</h3>
          
          {qualityReport.status === 'insufficient_data' ? (
            <div className="insufficient-data">
              <div className="warning-icon">⚠️</div>
              <div>
                <h4>Insufficient Data</h4>
                <p>{qualityReport.message || 'Insufficient data for testing'}</p>
                <p>Sample Size: {formatSampleSize(qualityReport.sample_size)}</p>
                <p>Required: {formatSampleSize(qualityReport.minimum_required || 1000)}</p>
              </div>
            </div>
          ) : (
            <div className="quality-metrics">
              <div className="metrics-grid">
                <div className="metric-card">
                  <div className="metric-label">Sample Size</div>
                  <div className="metric-value">{formatSampleSize(qualityReport.sample_size)}</div>
                </div>
                <div className="metric-card">
                  <div className="metric-label">Tests Run</div>
                  <div className="metric-value">{qualityReport.tests_run}</div>
                </div>
                <div className="metric-card">
                  <div className="metric-label">Tests Passed</div>
                  <div className="metric-value">{qualityReport.tests_passed}</div>
                </div>
                <div className="metric-card">
                  <div className="metric-label">Pass Rate</div>
                  <div 
                    className="metric-value pass-rate"
                    style={{ color: getPassRateColor(qualityReport.pass_rate) }}
                  >
                    {(qualityReport.pass_rate * 100).toFixed(1)}%
                  </div>
                </div>
              </div>

              <div className="recommendation">
                <h4>Recommendation</h4>
                <p>{qualityReport.recommendation}</p>
              </div>

              {/* Test Results */}
              {qualityReport.results && qualityReport.results.length > 0 && (
                <div className="test-results">
                  <h4>Test Results</h4>
                  <div className="results-table">
                    <div className="table-header">
                      <div>Test Name</div>
                      <div>Status</div>
                      <div>P-Value</div>
                      <div>Sample Size</div>
                    </div>
                    {qualityReport.results.map((result, index) => (
                      <div key={index} className="table-row">
                        <div className="test-name">{result.test_name}</div>
                        <div className={`test-status ${result.passed ? 'pass' : 'fail'}`}>
                          {result.passed ? 'PASS' : 'FAIL'}
                        </div>
                        <div className="p-value">{formatPValue(result.p_value)}</div>
                        <div className="sample-size">{formatSampleSize(result.sample_size)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Quick Actions */}
      <div className="quick-actions">
        <h3>Quick Actions</h3>
        <div className="action-buttons">
          <button 
            onClick={async () => {
              try {
                setCollectingData(true);
                setError(null);
                // Collect larger sample to make impact more visible (5000 bytes)
                await apiService.collectRandomnessSample(5000);
                // Wait a moment for data to be processed
                await new Promise(resolve => setTimeout(resolve, 500));
                await fetchData();
              } catch (err) {
                setError('Failed to collect sample');
              } finally {
                setCollectingData(false);
              }
            }}
            className="action-btn"
            disabled={collectingData || loading}
          >
            {collectingData ? (
              <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <WizardSpinner size={16} />
                Collecting...
              </span>
            ) : (
              'Collect Sample (5K bytes)'
            )}
          </button>
          <button 
            onClick={async () => {
              try {
                const reportData = await apiService.getRandomnessQualityReport();
                const blob = new Blob([JSON.stringify(reportData, null, 2)], { type: 'application/json' });
                const url = window.URL.createObjectURL(blob);
                window.open(url, '_blank');
              } catch (err) {
                setError('Failed to fetch report');
              }
            }}
            className="action-btn"
          >
            View Raw Data
          </button>
        </div>
      </div>
    </div>
  );
};

export default RandomnessMonitor;